// Detecção de novo deploy.
//
// Quando a VM recebe uma imagem nova, os chunks com hash antigo somem. Uma aba
// aberta que tenta carregar um chunk antigo recebe 404 — isso NÃO é um erro da
// aplicação e não deve subir para o error boundary. O certo é avisar que saiu
// versão nova e recarregar.
//
// O Vite emite `vite:preloadError` nesse caso; import() dinâmico que falha
// também produz mensagens reconhecíveis, e nem todo navegador passa pelo evento
// do Vite — por isso checamos as duas coisas.

const PRELOAD_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'failed to import',
  'importing a module script failed',
  'unable to preload css',
  'dynamically imported module', // Safari
]

export function isDeployError(error: unknown): boolean {
  if (!error) return false
  if (typeof error === 'object' && (error as { __deploy?: boolean }).__deploy) return true

  const message = error instanceof Error ? error.message : String(error)
  const haystack = message.toLowerCase()
  return PRELOAD_PATTERNS.some((p) => haystack.includes(p))
}

type Listener = () => void
const listeners = new Set<Listener>()
let detected = false

export function onDeployDetected(listener: Listener): () => void {
  listeners.add(listener)
  if (detected) listener()
  return () => listeners.delete(listener)
}

export function notifyDeployDetected(): void {
  if (detected) return
  detected = true
  for (const listener of listeners) listener()
}

export function deployWasDetected(): boolean {
  return detected
}

/** Liga os detectores globais. Chamado uma vez no main.tsx. */
export function installDeployDetector(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Impede o erro padrão do Vite (que recarrega sem avisar nada ao usuário).
    event.preventDefault()
    notifyDeployDetected()
  })

  window.addEventListener('error', (event) => {
    if (isDeployError(event.error ?? event.message)) {
      event.preventDefault()
      notifyDeployDetected()
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isDeployError(event.reason)) {
      event.preventDefault()
      notifyDeployDetected()
    }
  })
}
